import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { StepTypeSchema, StepCategorySchema } from './step.schema.js';
import { MediaItemSchema } from './media.schema.js';
// Extend Zod with OpenAPI
extendZodWithOpenApi(z);
// UUID validation
const uuidSchema = z.string().uuid('Invalid UUID format');
// Create lesson input schema
export const CreateLessonSchema = z.object({
    title: z
        .string()
        .min(1, 'Title is required')
        .max(255, 'Title must be less than 255 characters')
        .trim()
        .openapi({
        description: 'Lesson title',
        example: 'Introduction to Variables',
    }),
    description: z
        .string()
        .max(5000, 'Description must be less than 5000 characters')
        .nullable()
        .optional()
        .openapi({
        description: 'Detailed lesson description',
        example: 'Learn about variable types and how to use them effectively',
    }),
    isLive: z.boolean().optional().default(true).openapi({
        description: 'Whether the lesson is visible to learners',
        example: true,
    }),
    passPct: z
        .number()
        .min(0, 'Pass percentage must be at least 0')
        .max(100, 'Pass percentage must be at most 100')
        .optional()
        .default(50)
        .openapi({
        description: 'Minimum score percentage required to pass the lesson',
        example: 50,
    }),
});
// Lesson step input schema (for inline step data when updating a lesson)
export const LessonStepInputSchema = z.object({
    stepId: z.string().uuid(),
    type: StepTypeSchema,
    prompt: z.unknown(),
    config: z.unknown().nullable(),
    media: z.unknown().nullable(),
    points: z.number().int().min(0).default(0),
    explanation: z.string().nullable().optional(),
    category: StepCategorySchema,
    position: z.number().int().min(0),
});
// Update lesson input schema (all fields optional)
export const UpdateLessonSchema = z.object({
    title: z
        .string()
        .min(1, 'Title cannot be empty')
        .max(255, 'Title must be less than 255 characters')
        .trim()
        .optional()
        .openapi({
        description: 'Updated lesson title',
        example: 'Advanced Variables',
    }),
    description: z
        .string()
        .max(5000, 'Description must be less than 5000 characters')
        .nullable()
        .optional()
        .openapi({
        description: 'Updated lesson description',
    }),
    isLive: z.boolean().optional().openapi({
        description: 'Whether the lesson is visible to learners',
        example: true,
    }),
    passPct: z
        .number()
        .min(0, 'Pass percentage must be at least 0')
        .max(100, 'Pass percentage must be at most 100')
        .optional()
        .openapi({
        description: 'Minimum score percentage required to pass the lesson',
        example: 50,
    }),
    stepIds: z
        .array(uuidSchema)
        .optional()
        .openapi({
        description: 'Optional array of learning step IDs to associate with the lesson',
        example: ['123e4567-e89b-12d3-a456-426614174000'],
    }),
    reviseStepIds: z
        .array(uuidSchema)
        .optional()
        .openapi({
        description: 'Optional array of revision step IDs to associate with the lesson',
        example: ['123e4567-e89b-12d3-a456-426614174000'],
    }),
    steps: z
        .array(LessonStepInputSchema)
        .optional()
        .openapi({
        description: 'Optional array of step data for inline lesson step updates',
    }),
    courseVersionId: z.string().uuid().optional(),
});
// Lesson content step input schema (for atomic lesson save — typed media, no category/position)
export const LessonContentStepInputSchema = z.object({
    stepId: z.string().uuid().optional(),
    type: StepTypeSchema,
    prompt: z.unknown(),
    config: z.unknown().nullable(),
    media: z.array(MediaItemSchema).nullable(),
    points: z.number().int().min(0).default(0),
    explanation: z.string().nullable().optional(),
});
// Full lesson content schema (for atomic lesson save endpoint)
export const LessonContentSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    isLive: z.boolean().optional(),
    passPct: z.number().min(0).max(100).optional(),
    learnSteps: z.array(LessonContentStepInputSchema),
    reviseSteps: z.array(LessonContentStepInputSchema),
    deletedStepIds: z.array(z.string().uuid()).optional(),
});
// List lessons query parameters
export const ListLessonsQuerySchema = z.object({
    page: z
        .string()
        .optional()
        .default('1')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0, 'Page must be greater than 0'),
    pageSize: z
        .string()
        .optional()
        .default('10')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0 && val <= 100, 'Page size must be between 1 and 100'),
    sortBy: z.enum(['createdAt', 'updatedAt', 'title']).optional().default('createdAt').openapi({
        description: 'Field to sort results by',
        example: 'createdAt',
    }),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc').openapi({
        description: 'Sort direction',
        example: 'desc',
    }),
    topicId: uuidSchema.optional().openapi({
        description: 'Filter lessons by topic ID',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// Lesson ID parameter validation
export const LessonIdParamSchema = z.object({
    lessonId: uuidSchema.openapi({
        description: 'Unique identifier of the lesson',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// Get lesson query parameters (for include)
export const GetLessonQuerySchema = z.object({
    include: z.enum(['steps']).optional().openapi({
        description: 'Include related data: steps (all steps for this lesson)',
        example: 'steps',
    }),
    courseVersionId: z.string().uuid().optional(),
});
// Lesson Schema (response — includes content from version tables)
export const LessonSchema = z
    .object({
    id: uuidSchema.openapi({
        description: 'Unique lesson identifier',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    title: z
        .string()
        .openapi({ description: 'Lesson title', example: 'Introduction to Variables' }),
    description: z.string().nullable().openapi({ description: 'Lesson description' }),
    isLive: z.boolean().openapi({
        description: 'Whether the lesson is visible to learners',
        example: true,
    }),
    version: z.number().int().openapi({ description: 'Content version number', example: 1 }),
    createdAt: z.string().openapi({ description: 'Timestamp when the lesson was created' }),
    updatedAt: z.string().openapi({ description: 'Timestamp when the lesson was last updated' }),
    passPct: z.number().nullable().openapi({
        description: 'Minimum score percentage required to pass the lesson',
        example: 50,
    }),
})
    .openapi('Lesson');
// --- Granular Step CRUD ---
export const CreateStepInLessonSchema = z.object({
    type: StepTypeSchema,
    category: StepCategorySchema,
    position: z.number().int().min(0),
    prompt: z.unknown().optional(),
    config: z.unknown().optional(),
    media: z.unknown().nullable().optional(),
    points: z.number().int().min(0).optional(),
    explanation: z.string().nullable().optional(),
    diagram: z.unknown().optional(),
    courseVersionId: z.string().uuid().optional(),
});
export const UpdateStepInLessonSchema = z.object({
    prompt: z.unknown().optional(),
    config: z.unknown().optional(),
    media: z.unknown().nullable().optional(),
    points: z.number().int().min(0).optional(),
    explanation: z.string().nullable().optional(),
    diagram: z.unknown().optional(),
    isLive: z.boolean().optional(),
    courseVersionId: z.string().uuid().optional(),
});
export const LessonStepIdParamSchema = z.object({
    lessonId: z.string().uuid(),
    stepId: z.string().uuid(),
});
export const ReorderStepsSchema = z.object({
    steps: z.array(z.object({
        stepId: z.string().uuid(),
        position: z.number().int().min(0),
        category: StepCategorySchema,
    })),
    courseVersionId: z.string().uuid().optional(),
});
export const DeleteStepQuerySchema = z.object({
    courseVersionId: z.string().uuid().optional(),
});
// --- Publish ---
export const PublishLessonSchema = z.object({
    notes: z.string().max(500).optional(),
});
export const ListLessonVersionsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
/**
 * Checks whether an UpdateStepInLessonInput contains any versioned content changes
 * (prompt, config, media, points, explanation). Used to distinguish content edits
 * (which require the draft/versioning flow) from identity-only changes like isLive.
 */
export function hasStepContentChange(changes) {
    return (changes.prompt !== undefined ||
        changes.config !== undefined ||
        changes.media !== undefined ||
        changes.points !== undefined ||
        changes.explanation !== undefined ||
        changes.diagram !== undefined);
}
//# sourceMappingURL=lesson.schema.js.map